import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Sparkles,
  Users,
  Activity,
  Image as ImageIcon,
  Receipt,
  ExternalLink,
  Quote,
  CheckCircle2,
  CircleDashed,
  Clock,
} from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";
import { ClaimedFactsPanel } from "../components/ClaimedFactsPanel";

/** Step 24: rich place dossier at /places/:id. Peer to the person
 *  dossier. Every panel renders an empty state when its data is cold
 *  so a freshly resolved place still shows a coherent page. */
interface PlaceMeta {
  id: string;
  name: string;
  level: string;
  admin_chain?: string[];
  raw_variants?: string[];
  map_url?: string;
  resolutions?: number;
}
interface Visit {
  start: string;
  end: string;
  people: Array<{ id: string; name: string }>;
  transactions: Array<Record<string, unknown>>;
  photo_count: number;
}

/** Step 25: dossier shape returned by GET /api/places/:id/dossier.
 *  Mirrors the aggregateDossier() output from Q6's entity-aware
 *  Recall engine when a graph_node resolves; otherwise the backend
 *  synthesizes the same shape from place_resolutions so the page
 *  always renders. */
interface DossierResp {
  ok: boolean;
  place: {
    id: string;
    name: string;
    level: string;
    inferred: boolean;
    avg_confidence: number;
    resolutions: number;
  };
  intent: string;
  aggregation: {
    kind?: string;
    subject?: string;
    visit_count?: number;
    first_visit?: string | null;
    last_visit?: string | null;
    top_people_co_mentioned?: Array<{ name: string; count: number }>;
    recent_activities?: Array<{ date: string; kind: string; snippet: string }>;
  };
  citations: Array<{
    ref_kind: string;
    ref_id: string;
    label: string;
    date: string | null;
  }>;
  answer: string;
  answer_source: "llm" | "deterministic";
  resolved_to_graph_node: boolean;
}

/** Map a citation to its click-through surface, matching the chip
 *  routing in MessageBubble.tsx so a dossier citation behaves the
 *  same way as one rendered in a Recall answer. */
function citationHref(c: { ref_kind: string; ref_id: string; date: string | null }): string {
  switch (c.ref_kind) {
    case "engagement":
      return "/needs-attention";
    case "packet":
      return `/explore?packet=${encodeURIComponent(c.ref_id)}`;
    case "thread":
    case "conversation":
      return `/conversations?thread=${encodeURIComponent(c.ref_id)}`;
    case "visit":
    case "place_resolution":
      return `/explore?place=${encodeURIComponent(c.ref_id)}`;
    case "edge":
    case "graph_edge":
      return "/explore";
    default:
      return "/explore";
  }
}

function useJson<T>(url: string | null): T | null {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    fetch(url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled) setData(j);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [url]);
  return data;
}

export function PlaceDossier() {
  const { id } = useParams<{ id: string }>();
  const base = id ? `/api/places/${encodeURIComponent(id)}` : null;
  const meta = useJson<{ ok: boolean; place: PlaceMeta; empty?: string }>(base);
  const visitsR = useJson<{ ok: boolean; visits: Visit[]; undated: number }>(
    base ? `${base}/visits` : null,
  );
  const peopleR = useJson<{
    ok: boolean;
    people: Array<{ id: string; name: string; count: number }>;
  }>(base ? `${base}/people` : null);
  const tsR = useJson<{ ok: boolean; series: Array<{ ym: string; n: number }> }>(
    base ? `${base}/timeseries?range=monthly` : null,
  );
  const photosR = useJson<{
    ok: boolean;
    photos: Array<{ id: string; captured_at: string; type: string }>;
  }>(base ? `${base}/photos?limit=20` : null);
  const summaryR = useJson<{ ok: boolean; summary: string; source: string }>(
    base ? `${base}/summary` : null,
  );
  const dossierR = useJson<DossierResp>(base ? `${base}/dossier` : null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  async function confirmPlace() {
    if (!base) return;
    setConfirming(true);
    try {
      const r = await fetch(`${base}/confirm`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (r.ok) setConfirmed(true);
    } catch {
      /* ignore */
    } finally {
      setConfirming(false);
    }
  }

  if (!meta)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 text-sm text-slate-400">
        Loading…
      </div>
    );

  const p = meta.place;
  const series = tsR?.series ?? [];
  const maxN = Math.max(1, ...series.map((s) => s.n));

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb
        segments={[
          { label: "Explore", href: "/explore" },
          { label: "Library", href: "/explore/library" },
          { label: "Places", href: "/places" },
          { label: p.name },
        ]}
      />

      <header className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-500 flex items-center justify-center text-white shadow-md flex-shrink-0">
          <MapPin className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {p.name}
          </h1>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-semibold">
              {p.level}
            </span>
            {dossierR?.place.inferred && !confirmed ? (
              <button
                type="button"
                onClick={confirmPlace}
                disabled={confirming}
                title="Memso inferred this place from low-confidence signals. Tap to confirm."
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-dashed border-amber-400 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 font-semibold transition-colors"
              >
                <CircleDashed className="w-3 h-3" />
                {confirming ? "Confirming…" : "Inferred · confirm"}
              </button>
            ) : (
              dossierR && (
                <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-semibold">
                  <CheckCircle2 className="w-3 h-3" />
                  Confirmed
                </span>
              )
            )}
            {p.admin_chain && p.admin_chain.length > 0 && (
              <span className="text-xs text-slate-400">
                {p.admin_chain.join(" › ")}
              </span>
            )}
            {p.map_url && (
              <a
                href={p.map_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400 hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> Map
              </a>
            )}
          </div>
        </div>
      </header>

      <TeachingBanner id="place_dossier_overview" />

      {id && (
        <ClaimedFactsPanel kind="place" id={id} title="What Memso knows about this place" />
      )}

      <section className="glass rounded-2xl border border-sky-200/70 dark:border-sky-800/40 backdrop-blur bg-gradient-to-br from-sky-50/60 via-white/30 to-cyan-50/40 dark:from-sky-900/15 dark:via-slate-800/20 dark:to-cyan-900/10 p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-sky-700 dark:text-sky-300 mb-2">
          <Sparkles className="w-4 h-4" />
          Memso says
          {dossierR?.answer_source === "llm" && (
            <span className="ml-auto text-[10px] text-sky-500/70 font-medium uppercase tracking-wider">
              Narrated
            </span>
          )}
        </div>
        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
          {dossierR?.answer ||
            summaryR?.summary ||
            meta.empty ||
            "Gathering what Memso knows about this place…"}
        </p>
      </section>

      <section className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
            Visits
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5">
            {dossierR?.aggregation.visit_count ?? "…"}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
            First visit
          </div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
            {dossierR?.aggregation.first_visit || "Not yet woven"}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500">
            Last visit
          </div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
            {dossierR?.aggregation.last_visit || "Not yet woven"}
          </div>
        </div>
      </section>

      {(dossierR?.aggregation.top_people_co_mentioned?.length ?? 0) > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
            <Users className="w-4 h-4 text-slate-400" />
            Top people co-mentioned
          </h2>
          <ul className="flex flex-wrap gap-2">
            {dossierR!.aggregation.top_people_co_mentioned!.map((pe) => (
              <li key={pe.name}>
                <Link
                  to={`/people?q=${encodeURIComponent(pe.name)}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 dark:bg-sky-900/30 hover:bg-sky-100 dark:hover:bg-sky-900/50 border border-sky-200/60 dark:border-sky-700/40 px-2.5 py-1 text-xs font-medium text-sky-800 dark:text-sky-200"
                >
                  {pe.name}
                  <span className="text-[10px] text-sky-500/80">×{pe.count}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(dossierR?.aggregation.recent_activities?.length ?? 0) > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
            <Clock className="w-4 h-4 text-slate-400" />
            Recent activities
          </h2>
          <ul className="space-y-2">
            {dossierR!.aggregation.recent_activities!.map((a, i) => (
              <li
                key={`${a.date}-${i}`}
                className="border-l-2 border-sky-300 dark:border-sky-700 pl-3"
              >
                <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <span className="font-mono">{a.date}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                    {a.kind}
                  </span>
                </div>
                <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5">
                  {a.snippet}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(dossierR?.citations?.length ?? 0) > 0 && (
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
            <Quote className="w-4 h-4 text-slate-400" />
            Sources
            <span className="ml-auto text-[10px] text-slate-400 font-normal">
              Tap to open the underlying capture
            </span>
          </h2>
          <ul className="flex flex-wrap gap-1.5">
            {dossierR!.citations.slice(0, 12).map((c, i) => (
              <li key={`${c.ref_kind}-${c.ref_id}-${i}`}>
                <Link
                  to={citationHref(c)}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 dark:bg-slate-700/60 hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-0.5 text-[11px] text-slate-700 dark:text-slate-200"
                >
                  <span className="text-[9px] uppercase tracking-wider text-slate-400">
                    {c.ref_kind}
                  </span>
                  {c.date && (
                    <span className="font-mono text-[10px] text-slate-500">
                      {c.date.slice(0, 10)}
                    </span>
                  )}
                  <span className="truncate max-w-[160px]">{c.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
          <Activity className="w-4 h-4 text-slate-400" />
          Visit frequency
        </h2>
        {series.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No dated visits yet.</p>
        ) : (
          <div className="flex items-end gap-1 h-16">
            {series.map((s) => (
              <div
                key={s.ym}
                title={`${s.ym}: ${s.n}`}
                className="flex-1 bg-sky-500/70 dark:bg-sky-400/60 rounded-t"
                style={{ height: `${Math.max(6, (s.n / maxN) * 100)}%` }}
              />
            ))}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
          <MapPin className="w-4 h-4 text-slate-400" />
          Visits
          {visitsR && visitsR.undated > 0 && (
            <span className="ml-auto text-[11px] text-slate-400 font-normal">
              +{visitsR.undated} undated
            </span>
          )}
        </h2>
        {!visitsR ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : visitsR.visits.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No dated visits woven yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {visitsR.visits.map((v, i) => (
              <li
                key={i}
                className="border-l-2 border-sky-300 dark:border-sky-700 pl-3"
              >
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {v.start.slice(0, 10)}
                  {v.end && v.end !== v.start ? ` → ${v.end.slice(0, 10)}` : ""}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  {v.people.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {v.people.map((pe) => pe.name).join(", ")}
                    </span>
                  )}
                  {v.photo_count > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" />
                      {v.photo_count} photo{v.photo_count === 1 ? "" : "s"}
                    </span>
                  )}
                  {v.transactions.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Receipt className="w-3 h-3" />
                      {v.transactions.length} transaction
                      {v.transactions.length === 1 ? "" : "s"}
                    </span>
                  )}
                  {v.people.length === 0 &&
                    v.photo_count === 0 &&
                    v.transactions.length === 0 && (
                      <span className="italic">No linked artifacts.</span>
                    )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
          <Users className="w-4 h-4 text-slate-400" />
          Linked people
        </h2>
        {!peopleR ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : peopleR.people.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No co-present people woven yet.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {peopleR.people.map((pe) => (
              <li key={pe.id}>
                <Link
                  to={`/people/${encodeURIComponent(pe.id)}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-200"
                >
                  {pe.name}
                  <span className="text-[10px] text-slate-400">×{pe.count}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
          <ImageIcon className="w-4 h-4 text-slate-400" />
          Recent photos
        </h2>
        {!photosR ? (
          <p className="text-xs text-slate-400">Loading…</p>
        ) : photosR.photos.length === 0 ? (
          <p className="text-xs text-slate-400 italic">
            No photos for this place. Connect the Photos source to see
            imagery from these dates.
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {photosR.photos.map((ph) => (
              <div
                key={ph.id}
                title={ph.captured_at}
                className="aspect-square rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center"
              >
                <ImageIcon className="w-5 h-5 text-slate-400" />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
